/**
 * Phase 9B.2 — Role helpers.
 *
 * Two thin utilities:
 *
 *  1. `useRoleCapabilities()` — derives the UI-visible capabilities
 *     from the currently-authenticated user's role. These booleans
 *     mirror the canonical route-guard rules in `App.js` (and thus
 *     the backend RBAC that guards each endpoint). They exist so
 *     the navigation config can hide nav-items that the current
 *     role cannot use — but they are **UX only**.
 *
 *  2. `<RoleGate>` — declarative visibility wrapper. Children are
 *     rendered only when the passed predicate is true.
 *
 * IMPORTANT — this is NOT authorization security. The backend
 * enforces every endpoint via `deps.py::require_current_user` and
 * the per-endpoint role gates. If the frontend rules diverge from
 * the backend the worst that happens is a link that leads to a
 * 403 page — data is never exposed.
 */
import { useMemo } from "react";
import { useAuth } from "@/auth/AuthContext";

const PLANNER_ROLES = ["owner", "manager", "teamlead"];
const MANAGER_PLUS = ["owner", "manager"];

/**
 * Return a stable object describing what the current user's role is
 * allowed to see in navigation. Mirrors `App.js` route guards; do not
 * duplicate the full backend RBAC matrix here.
 */
export function useRoleCapabilities() {
  const { user } = useAuth();
  const role = user?.role || null;

  return useMemo(() => {
    const isOwner = role === "owner";
    const isManager = role === "manager";
    const isTeamlead = role === "teamlead";
    const isEmployee = role === "employee";
    const isPlanner = PLANNER_ROLES.includes(role);

    return {
      role,
      isOwner,
      isManager,
      isTeamlead,
      isEmployee,
      isPlanner,
      // Route-guard-derived capabilities (see App.js).
      canRoster: isPlanner,
      canEmployees: isPlanner,
      canTeams: isPlanner,
      canLocations: isOwner,                          // /locations owner-only
      canPermissions: MANAGER_PLUS.includes(role),    // /permissions owner+manager
      canPayroll: MANAGER_PLUS.includes(role),        // /payroll owner+manager
      canPauseSettings: MANAGER_PLUS.includes(role),  // /pause-settings owner+manager
      canReports: isPlanner,
      canTimesheets: isPlanner,
      canLeaveApprove: isPlanner,                     // /requests, /swaps, /manager-requests
      canSettings: isOwner,                           // /settings owner-only
      canBilling: isOwner,                            // /billing owner-only
      // Personal workflows — every authenticated user has these
      hasPersonalRoster: !!role,
      hasPersonalLeave: !!role,
      hasPersonalSwaps: !!role,
      hasPersonalAvailability: !!role,
    };
  }, [role]);
}

/**
 * Declarative visibility gate for nav items and UI affordances.
 *
 * Usage:
 *   <RoleGate when={caps.canPayroll}><NavItem to="/payroll" /></RoleGate>
 *
 * Renders nothing when `when` is falsy. Do not use to hide security-
 * sensitive data — the backend already enforces that.
 */
export function RoleGate({ when, children, fallback = null }) {
  return when ? children : fallback;
}

export default useRoleCapabilities;
